import { Alert, Box, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { fetchWithError } from "src/react-query/fetchWithError";
import { queryKeys } from "src/react-query/queryKeys";
import { is } from "src/util/is";
import { BaseTable } from "../BaseTable/BaseTable";
import { publishedChargerColumns } from "./company.constants";

/**
 * Chargers exposed to this OCPI partner. The backend resolves the partner's
 * allowed_company list (expanding the "1" wildcard to every amiktechnologies company)
 * and returns every charger owned by those companies.
 */
export const PublishedChargers = ({ companyId }) => {
  const { data, isFetching, isError, error } = useQuery(
    queryKeys.companyPublishedChargers(null, companyId),
    fetchWithError,
    { enabled: !is.empty(companyId) }
  );

  const chargers = data?.data ?? [];
  const meta = data?.meta;

  if (isError) {
    return (
      <Alert severity="error">
        {error?.response?.data?.message ??
          "Could not load the chargers published to this company"}
      </Alert>
    );
  }

  return (
    <Box>
      <Stack spacing={0.5} mb={2}>
        <Typography variant="subtitle2" color="text.secondary">
          Chargers published to this OCPI partner, based on the allowed companies
          configured for it.
        </Typography>
        {meta && (
          <Typography variant="caption" color="text.secondary">
            Party ID: {meta.partyId ?? "-"} · Role:{" "}
            {meta.role?.toUpperCase() ?? "-"} · Allowed companies:{" "}
            {meta.allowedCompany?.length
              ? meta.allowedCompany.join(", ")
              : "none configured"}
          </Typography>
        )}
      </Stack>

      {!isFetching && is.empty(chargers) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No chargers are published to this company. Add company ids to its
          allowed companies list to publish chargers.
        </Alert>
      )}

      <BaseTable
        rows={chargers}
        columns={publishedChargerColumns}
        loading={isFetching}
        getRowId={(row) => row.chargepoint_pk}
        pageSize={10}
        isReadOnly
        showExportCsvBtn
        csvColumns={publishedChargerColumns}
        csvMorphemes="publishedChargers"
      />
    </Box>
  );
};
